import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Instagram, Linkedin, Facebook, Youtube, Check } from 'lucide-react';
import { CONTACT, PRODUCTS, ATMOSPHERE } from '../data/products';
import { useSearchParams } from 'react-router-dom';
import AtmoBg from '../components/AtmoBg';
import { localBusinessSchema } from '../lib/schema';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const PROJECT_TYPES = ['Luxury Resort', 'Boutique Hotel', 'Glamping', 'Farmhouse', 'Holiday Home', 'Weekend Home', 'Office Space', 'Retail / Pop-up', 'Rental Property', 'Other'];

const initial = { name: '', company: '', phone: '', email: '', country: '', project_type: '', message: '', honeypot: '' };

export default function Contact() {
  const [params] = useSearchParams();
  const productHint = params.get('product');
  const [form, setForm] = useState({ ...initial, message: productHint ? `I'm interested in the ${productHint.toUpperCase()} model.\n\n` : '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!form.name || !form.phone || !form.email || !form.country || !form.project_type || !form.message) {
      toast.error('Please complete the required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      toast.success('Thank you. Your request has been received.');
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Please review your submission.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-[#111] text-white">
      <Helmet>
        <title>Contact MODULAROOM</title>
        <meta name="description" content="Speak with the MODULAROOM team about your project. Book a consultation for luxury modular homes, capsule suites, modular resort rooms or a bespoke prefab cottage." />
        <link rel="canonical" href="https://modularoom.com/contact" />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
      </Helmet>

      <section className="relative overflow-hidden pt-40 md:pt-52 pb-16">
        <AtmoBg src={ATMOSPHERE.contactWarm} base="black" opacity={0.20} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#C7A36A]">Get in touch</div>
          <h1 className="font-display text-5xl md:text-7xl mt-5 tracking-tight leading-[0.98] max-w-4xl">
            Let&apos;s design the space you&apos;ll remember.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Tell us about your project. Our team will respond with a curated proposal, usually within one business day.
          </p>
        </div>
      </section>

      <section id="book" className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-14">
        <div className="md:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="border border-white/10 rounded-2xl p-10 md:p-14 bg-white/[0.02]">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C7A36A] text-[#111]"><Check size={22}/></div>
              <div className="mt-6 text-[11px] tracking-[0.32em] uppercase text-[#C7A36A]">Received</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight leading-[1.02]">Thank you for reaching out.</h2>
              <p className="mt-5 text-white/70 max-w-lg leading-relaxed">
                Your request has been received. A MODULAROOM specialist will get in touch shortly to discuss your project in detail.
              </p>
              <button onClick={() => setDone(false)} data-testid="contact-new" className="mt-8 rounded-full border border-white/30 px-6 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-white hover:text-[#111] transition-colors">
                Send another request
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <input className="hp-field" type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={update('honeypot')} name="website" />
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Name *</label>
                <input data-testid="input-name" className="editorial-input" value={form.name} onChange={update('name')} placeholder="Your full name" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Company</label>
                <input data-testid="input-company" className="editorial-input" value={form.company} onChange={update('company')} placeholder="Optional" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Phone *</label>
                <input data-testid="input-phone" className="editorial-input" value={form.phone} onChange={update('phone')} placeholder="+91 …" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Email *</label>
                <input data-testid="input-email" type="email" className="editorial-input" value={form.email} onChange={update('email')} placeholder="you@company.com" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Country *</label>
                <input data-testid="input-country" className="editorial-input" value={form.country} onChange={update('country')} placeholder="India / UAE / …" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Project Type *</label>
                <select data-testid="input-project" className="editorial-input" value={form.project_type} onChange={update('project_type')}>
                  <option value="" style={{ color: '#111' }}>Select…</option>
                  {PROJECT_TYPES.map((p) => <option key={p} value={p} style={{ color: '#111' }}>{p}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 mt-2">
                <label className="text-[10px] tracking-[0.28em] uppercase text-white/50">Message *</label>
                <textarea data-testid="input-message" className="editorial-input" rows={4} value={form.message} onChange={update('message')} placeholder="Tell us about your project, timeline and location." />
              </div>
              <div className="md:col-span-2 mt-4">
                <button data-testid="contact-submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-white text-[#111] px-8 py-4 text-[13px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors disabled:opacity-60">
                  {submitting ? 'Sending…' : 'Send request'} <ArrowRight size={16}/>
                </button>
                <div className="mt-4 text-[11px] text-white/40">We reply within 1 business day. Your details stay confidential.</div>
              </div>
              {productHint && <input type="hidden" data-testid="product-hint" value={productHint} readOnly />}
            </form>
          )}
        </div>

        <div className="md:col-span-5 space-y-10 md:pl-6 md:border-l md:border-white/10">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">Address</div>
            <div className="mt-4 text-white/85 leading-relaxed">
              {CONTACT.address.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">Phone</div>
            <a href={`tel:${CONTACT.phone}`} className="mt-4 block hover:text-[#C7A36A]">{CONTACT.phone}</a>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">Email</div>
            <a href={`mailto:${CONTACT.email}`} className="mt-4 block text-[#C7A36A]">{CONTACT.email}</a>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">Social</div>
            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { key: 'instagram', label: 'Instagram', Icon: Instagram },
                { key: 'linkedin',  label: 'LinkedIn',  Icon: Linkedin  },
                { key: 'facebook',  label: 'Facebook',  Icon: Facebook  },
                { key: 'youtube',   label: 'YouTube',   Icon: Youtube   },
              ].filter(({ key }) => CONTACT.socials[key]).map(({ key, label, Icon }) => (
                <a
                  key={key}
                  data-testid={`contact-${key}`}
                  href={CONTACT.socials[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`MODULAROOM on ${label}`}
                  className="p-2 rounded-full border border-white/20 hover:border-[#C7A36A] hover:text-[#C7A36A] transition-colors"
                >
                  <Icon size={16}/>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-32">
        <div className="rounded-2xl overflow-hidden border border-white/10">
          <iframe title="MODULAROOM Rajkot" src={CONTACT.mapEmbed} className="w-full h-[420px] md:h-[520px]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
        <div className="mt-6 flex justify-end">
          <a data-testid="get-directions" href={CONTACT.mapLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-white hover:text-[#111] transition-colors">Get Directions <ArrowRight size={14}/></a>
        </div>
      </section>
    </main>
  );
}
